import { i18next } from "@translations/oarepo_ui/i18next";
import _get from "lodash/get";
import _startCase from "lodash/startCase";
import React, { useCallback, useRef, useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Message, Button, Icon } from "semantic-ui-react";
import { useFormTabs, useFieldData, useFormFeedback } from "../../hooks";
import {
  findSectionIndexForFieldPath,
  isErrorObject,
  flattenToPathValueArray,
} from "../../util";
import {
  DISCARD_PID_FAILED,
  DRAFT_DELETE_FAILED,
  DRAFT_HAS_VALIDATION_ERRORS,
  DRAFT_PREVIEW_FAILED,
  DRAFT_PREVIEW_FAILED_WITH_VALIDATION_ERRORS,
  DRAFT_PUBLISH_FAILED,
  DRAFT_PUBLISH_FAILED_WITH_VALIDATION_ERRORS,
  DRAFT_SAVE_FAILED,
  DRAFT_SAVE_SUCCEEDED,
  DRAFT_LOADED_WITH_VALIDATION_ERRORS,
  DRAFT_SUBMIT_REVIEW_FAILED,
  DRAFT_SUBMIT_REVIEW_FAILED_WITH_VALIDATION_ERRORS,
  FILE_IMPORT_FAILED,
  FILE_UPLOAD_SAVE_DRAFT_FAILED,
  RESERVE_PID_FAILED,
} from "@js/invenio_rdm_records/src/deposit/state/types";
import PropTypes from "prop-types";
import { clearErrors } from "../../../forms/state/deposit/actions";
import {
  DRAFT_PUBLISH_REQUEST_FAILED,
  DRAFT_PUBLISH_REQUEST_FAILED_WITH_VALIDATION_ERRORS,
} from "../../../forms/state/deposit/types";
import { scrollToElement } from "../../../util";

const ACTIONS = {
  [DRAFT_SAVE_SUCCEEDED]: {
    feedback: "positive",
    message: i18next.t("Draft successfully saved."),
  },
  [DRAFT_HAS_VALIDATION_ERRORS]: {
    feedback: "warning",
    message: i18next.t("Draft saved with validation feedback in:"),
  },
  [DRAFT_LOADED_WITH_VALIDATION_ERRORS]: {
    feedback: "warning",
    message: i18next.t("Draft has validation feedback in:"),
  },
  [DRAFT_SAVE_FAILED]: {
    feedback: "negative",
    message: i18next.t(
      "The draft was not saved. Please try again. If the problem persists, contact user support."
    ),
  },
  [DRAFT_PUBLISH_FAILED]: {
    feedback: "negative",
    message: i18next.t(
      "The draft was not published. Please try again. If the problem persists, contact user support."
    ),
  },
  [DRAFT_PUBLISH_REQUEST_FAILED]: {
    feedback: "negative",
    message: i18next.t(
      "Publish request could not be submitted. Please try again. If the problem persists, contact user support."
    ),
  },
  [DRAFT_PUBLISH_REQUEST_FAILED_WITH_VALIDATION_ERRORS]: {
    feedback: "warning",
    message: i18next.t(
      "Request to publish draft could not be executed due to validation errors:"
    ),
  },
  [DRAFT_PUBLISH_FAILED_WITH_VALIDATION_ERRORS]: {
    feedback: "negative",
    message: i18next.t(
      "The draft was not published. Draft saved with validation feedback in:"
    ),
  },
  [DRAFT_SUBMIT_REVIEW_FAILED]: {
    feedback: "negative",
    message: i18next.t(
      "The draft was not submitted for review. Please try again. If the problem persists, contact user support."
    ),
  },
  [DRAFT_SUBMIT_REVIEW_FAILED_WITH_VALIDATION_ERRORS]: {
    feedback: "negative",
    message: i18next.t(
      "The draft was not submitted for review. Draft saved with validation feedback in"
    ),
  },
  [DRAFT_DELETE_FAILED]: {
    feedback: "negative",
    message: i18next.t(
      "Draft deletion failed. Please try again. If the problem persists, contact user support."
    ),
  },
  [DRAFT_PREVIEW_FAILED]: {
    feedback: "negative",
    message: i18next.t(
      "Draft preview failed. Please try again. If the problem persists, contact user support."
    ),
  },
  [DRAFT_PREVIEW_FAILED_WITH_VALIDATION_ERRORS]: {
    feedback: "negative",
    message: i18next.t(
      "Unable to display preview. Please correct validation issues in:"
    ),
  },
  [RESERVE_PID_FAILED]: {
    feedback: "negative",
    message: i18next.t(
      "Identifier reservation failed. Please try again. If the problem persists, contact user support."
    ),
  },
  [DISCARD_PID_FAILED]: {
    feedback: "negative",
    message: i18next.t(
      "Identifier could not be discarded. Please try again. If the problem persists, contact user support."
    ),
  },
  [FILE_UPLOAD_SAVE_DRAFT_FAILED]: {
    feedback: "negative",
    message: i18next.t(
      "Draft save failed before file upload. Please try again. If the problem persists, contact user support."
    ),
  },
  [FILE_IMPORT_FAILED]: {
    feedback: "negative",
    message: i18next.t(
      "Files import from the previous version failed. Please try again. If the problem persists, contact user support."
    ),
  },
};

const FEEDBACK_COLORS = {
  positive: "green",
  warning: "yellow",
  negative: "red",
  info: "blue",
};

const FEEDBACK_ICONS = {
  positive: "check circle",
  warning: "warning sign",
  negative: "times circle",
  info: "info circle",
};

// function to turn last part of fieldPath from form camelCase to Camel Case
const titleCase = (fieldPath) =>
  _startCase(fieldPath.split(".")[fieldPath.split(".").length - 1]);

export const CustomMessage = ({ children = null, ...uiProps }) => {
  const dispatch = useDispatch();
  const handleClearErrors = useCallback(
    () => dispatch(clearErrors()),
    [dispatch]
  );

  return (
    <Message
      onDismiss={handleClearErrors}
      className="mb-5 form-feedback"
      {...uiProps}
    >
      {children}
    </Message>
  );
};

CustomMessage.propTypes = {
  // eslint-disable-next-line react/require-default-props
  children: PropTypes.node,
};

const ErrorMessageItem = ({ error }) => {
  const { getFieldData } = useFieldData();
  const label = getFieldData({
    fieldPath: error.fieldPath,
    fieldRepresentation: "text",
  })?.label;

  const errorMessage =
    label ||
    // ugly hack, but simply the path for file validation errors is completely
    // different and there does not seem to be a reasonable way to make translations
    // it is not clear can there be other validation errors for files than the one below
    (error.fieldPath === "files.enabled"
      ? i18next.t("Files")
      : titleCase(error.fieldPath));

  // Handle both old format (string) and new format (object with message)
  const errorValue = isErrorObject(error.value)
    ? error.value.message
    : error.value;

  return `${errorMessage}: ${errorValue}`;
};

ErrorMessageItem.propTypes = {
  error: PropTypes.object.isRequired,
};

export const FormFeedback = ({ actions = {}, sections = [] }) => {
  const errors = useSelector((state) => state.deposit.errors);

  const actionState = useSelector((state) => state.deposit.actionState);
  const formFeedbackMessage = useSelector(
    (state) => state.deposit.formFeedbackMessage
  );

  const { activeStep, setActiveStep } = useFormTabs() || {};
  const timeoutRef = useRef(null);
  const allActions = { ...ACTIONS, ...actions };
  const flattenedErrors = flattenToPathValueArray(errors);
  const feedbackType = _get(allActions, [actionState, "feedback"]);
  const color = FEEDBACK_COLORS[feedbackType];

  const message = _get(allActions, [actionState, "message"]);
  const backendErrorMessage = errors.message || errors._schema;
  const headerMessage = formFeedbackMessage || backendErrorMessage || message;

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleErrorClick = useCallback(
    (fieldPath) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      if (setActiveStep && activeStep !== undefined && sections.length > 0) {
        const sectionIndex = findSectionIndexForFieldPath(sections, fieldPath);
        if (sectionIndex >= 0 && sectionIndex !== activeStep) {
          setActiveStep(sectionIndex);
          timeoutRef.current = setTimeout(
            () => scrollToElement(fieldPath),
            100
          );
          return;
        }
      }
      scrollToElement(fieldPath);
    },
    [activeStep, setActiveStep, sections]
  );
  if (!headerMessage) return null;
  return (
    <CustomMessage color={color}>
      <Message.Header>{headerMessage}</Message.Header>
      {flattenedErrors?.length > 0 && (
        <Message.List>
          {flattenedErrors?.map((error, index) => (
            <Message.Item
              onClick={() => handleErrorClick(error.fieldPath)}
              key={`${error.fieldPath}-${index}`} // eslint-disable-line react/no-array-index-key
            >
              <ErrorMessageItem error={error} />
            </Message.Item>
          ))}
        </Message.List>
      )}
    </CustomMessage>
  );
};

FormFeedback.propTypes = {
  actions: PropTypes.object,
  sections: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      includesPaths: PropTypes.arrayOf(PropTypes.string),
      saveOnTabChange: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
      sectionCompletion: PropTypes.func,
      sectionCompletionThreshold: PropTypes.number,
      /** (formik, reduxState) => boolean — when truthy on the active section, tab navigation is blocked. */
      lockTabChange: PropTypes.func,
      /** component({ record, formConfig, activeStep, next, back, initialRecord }) => ReactNode */
      component: PropTypes.func.isRequired,
    })
  ),
};

export const FormFeedbackPanel = ({ actions = {}, sections = [] }) => {
  const [isOpen, setIsOpen] = useState(false);
  const errors = useSelector((state) => state.deposit.errors);
  const actionState = useSelector((state) => state.deposit.actionState);
  const formFeedbackMessage = useSelector(
    (state) => state.deposit.formFeedbackMessage
  );

  const { dismissed, setDismissed } = useFormFeedback() || {};

  const { activeStep, setActiveStep } = useFormTabs() || {};
  const timeoutRef = useRef(null);
  const dialogRef = useRef(null);
  const allActions = { ...ACTIONS, ...actions };
  const handleDismiss = useCallback(() => setDismissed(true), [setDismissed]);
  const flattenedErrors = flattenToPathValueArray(errors);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (isOpen && !dialog.open) dialog.showModal();
    else if (!isOpen && dialog.open) dialog.close();
  }, [isOpen]);

  const message = _get(allActions, [actionState, "message"]);
  const feedbackType = _get(allActions, [actionState, "feedback"]);
  const backendErrorMessage = errors.message || errors._schema;
  const headerMessage = formFeedbackMessage || backendErrorMessage || message;
  const hasErrors = flattenedErrors?.length > 0;

  const handleErrorClick = useCallback(
    (fieldPath) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      setIsOpen(false);

      if (setActiveStep && activeStep !== undefined && sections.length > 0) {
        const sectionIndex = findSectionIndexForFieldPath(sections, fieldPath);
        if (sectionIndex >= 0 && sectionIndex !== activeStep) {
          setActiveStep(sectionIndex);
          timeoutRef.current = setTimeout(
            () => scrollToElement(fieldPath),
            100
          );
          return;
        }
      }
      scrollToElement(fieldPath);
    },
    [activeStep, setActiveStep, sections]
  );

  if (!headerMessage || dismissed) return null;

  return (
    <>
      <Message
        {...{ [feedbackType || "warning"]: true }}
        onDismiss={handleDismiss}
        className="form-feedback-inline"
        data-testid="form-feedback-inline"
      >
        <Icon name={FEEDBACK_ICONS[feedbackType] || "warning sign"} />
        {headerMessage}
        {hasErrors && (
          <Button
            type="button"
            size="mini"
            basic
            onClick={() => setIsOpen((prev) => !prev)}
            aria-expanded={isOpen}
            aria-controls="form-feedback-panel"
            data-testid="form-feedback-summary-button"
            className="form-feedback-summary-button"
          >
            <Icon name="list" />
            {i18next.t("Summary")} ({flattenedErrors.length})
          </Button>
        )}
      </Message>

      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */}
      <dialog
        ref={dialogRef}
        id="form-feedback-panel"
        className="form-feedback-panel"
        aria-labelledby="form-feedback-panel-title"
        onClose={() => setIsOpen(false)}
        onClick={(e) => {
          if (e.target === dialogRef.current) setIsOpen(false);
        }}
        data-testid="form-feedback-panel"
      >
        <div className="form-feedback-panel-header">
          <strong id="form-feedback-panel-title">{headerMessage}</strong>
          <Button
            type="button"
            icon="close"
            size="mini"
            basic
            onClick={() => setIsOpen(false)}
            aria-label={i18next.t("Close error summary")}
            data-testid="form-feedback-panel-close"
          />
        </div>
        {hasErrors && (
          <div className="form-feedback-panel-content">
            <Message.List>
              {flattenedErrors.map((error, index) => (
                <Message.Item
                  key={`${error.fieldPath}-${index}`} // eslint-disable-line react/no-array-index-key
                  className="form-feedback-panel-item"
                >
                  <a
                    href={`#${error.fieldPath}`}
                    onClick={(e) => {
                      e.preventDefault();
                      handleErrorClick(error.fieldPath);
                    }}
                  >
                    <ErrorMessageItem error={error} />
                  </a>
                </Message.Item>
              ))}
            </Message.List>
          </div>
        )}
      </dialog>
    </>
  );
};

FormFeedbackPanel.propTypes = {
  actions: PropTypes.object,
  sections: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      includesPaths: PropTypes.arrayOf(PropTypes.string),
      saveOnTabChange: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
      /** component({ record, formConfig, activeStep, next, back, initialRecord }) => ReactNode */
      component: PropTypes.func.isRequired,
    })
  ),
};

export default FormFeedback;
